import * as React from "react";
import type { DataTableFilterableColumn, DataTableSearchableColumn } from "@/types/DataTable";
import { flexRender, type ColumnDef, type Table as TanstackTable } from "@tanstack/react-table";

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { DataTableAdvancedToolbar } from "./advanced/data-table-advanced-toolbar";
import { DataTableFloatingBar } from "./data-table-floating-bar";
import { DataTablePagination } from "./data-table-pagination";
import { DataTableToolbar } from "./data-table-toolbar";

interface DataTableProps<TData, TValue> {
    /**
     * The table instance returned from useDataTable hook with pagination, sorting, filtering, etc.
     * @type TanstackTable<TData>
     */
    table: TanstackTable<TData>;

    /**
     * The columns of the table
     * @default []
     * @type ColumnDef<TData, TValue>[]
     */
    columns: ColumnDef<TData, TValue>[];

    /**
     * The searchable columns of the table
     * @default []
     * @type {id: keyof TData, title: string}[]
     * @example searchableColumns={[{ id: "title", title: "titles" }]}
     */
    searchableColumns?: DataTableSearchableColumn<TData>[];

    /**
     * The filterable columns of the table. When provided, renders dynamic faceted filters, and the advancedFilter prop is ignored.
     * @default []
     * @type {id: keyof TData, title: string, options: { label: string, value: string, icon?: React.ComponentType<{ className?: string }> }[]}[]
     * @example filterableColumns={[{ id: "status", title: "Status", options: ["todo", "in-progress", "done", "canceled"]}]}
     */
    filterableColumns?: DataTableFilterableColumn<TData>[];

    /**
     * Show notion like filters when enabled
     * @default false
     * @type boolean
     */
    advancedFilter?: boolean;

    /**
     * The content to render in the floating bar on row selection, at the bottom of the table. When null, the floating bar is not rendered.
     * The datTable instance is passed as a prop to the floating bar content.
     * @default null
     * @type React.ReactNode | null
     * @example floatingBarContent={TasksTableFloatingBarContent(dataTable)}
     */
    floatingBarContent?: React.ReactNode | null;

    /**
     * The action to delete rows
     * @default undefined
     * @type React.MouseEventHandler<HTMLButtonElement> | undefined
     * @example deleteRowsAction={(event) => deleteSelectedRows(dataTable, event)}
     */
    deleteRowsAction?: React.MouseEventHandler<HTMLButtonElement>;

    newRowButton?: React.ReactNode;

    actionButton?: React.ReactNode;
}

export function DataTable<TData, TValue>({
    table,
    columns,
    searchableColumns = [],
    filterableColumns = [],
    advancedFilter = false,
    floatingBarContent,
    deleteRowsAction,
    newRowButton,
    actionButton,
}: DataTableProps<TData, TValue>) {
    return (
        <div className="w-full space-y-2.5 overflow-auto">
            {advancedFilter ? (
                <DataTableAdvancedToolbar
                    table={table}
                    filterableColumns={filterableColumns}
                    searchableColumns={searchableColumns}
                />
            ) : (
                <DataTableToolbar
                    table={table}
                    filterableColumns={filterableColumns}
                    searchableColumns={searchableColumns}
                    deleteRowsAction={deleteRowsAction}
                    newRowButton={newRowButton}
                    actionButton={actionButton}
                />
            )}
            <div className="mx-1 rounded-md border">
                <Table>
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <TableHead
                                            key={header.id}
                                            className={(header.column.columnDef.meta as any)?.className}
                                        >
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(header.column.columnDef.header, header.getContext())}
                                        </TableHead>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow key={row.id} data-state={row.getIsSelected() && "selected"}>
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell 
                                            key={cell.id}
                                            className={(cell.column.columnDef.meta as any)?.className}
                                        >
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} className="h-24 text-center">
                                    No results.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            <div className="space-y-2.5">
                <DataTablePagination table={table} />
                {floatingBarContent ? (
                    <DataTableFloatingBar table={table}>{floatingBarContent}</DataTableFloatingBar>
                ) : null}
            </div>
        </div>
    );
}
